<template>
    <section-frame
        :class="mergedFrameClass"
        :style="mergedFrameStyle"
        :items="sectionFrameItems"
        :more-icon-name="moreIconName"
        :more-text="moreText"
        :more-url="moreUrl"
        :show-header="showHeader"
        :show-more="showMore"
        :show-sidebar="showSidebar"
        :sidebar-active-id="internalSidebarActiveId"
        :sidebar-active-scroll-behavior="sidebarActiveScrollBehavior"
        :sidebar-auto-scroll-active="sidebarAutoScrollActive"
        :sidebar-badge-variant="sidebarBadgeVariant"
        :sidebar-context="sidebarContext"
        :sidebar-items="sidebarItems"
        :sidebar-position="sidebarPosition"
        :title="title"
        :empty-state="emptyState"
        :empty-retry-text="emptyRetryText"
        :show-empty-retry="showEmptyRetry"
        @change-sidebar="handleSidebarChange"
        @retry="handleRetry"
    >
        <template #title>
            <h2 class="section-frame__title">{{ title }}</h2>
        </template>

        <template v-if="slots.content" #content="contentSlotProps">
            <slot name="content" v-bind="contentSlotProps"></slot>
        </template>

        <template #item="slotProps">
            <slot v-if="slots.item" name="item" v-bind="slotProps"></slot>
            <a
                v-else
                class="section-frame__link"
                :href="resolveProductUrl(slotProps.item)"
            >
                <product-card
                    :id="resolveProductId(slotProps.item)"
                    :product-id="resolveProductId(slotProps.item)"
                    :product-name="resolveProductName(slotProps.item)"
                    :product-code="resolveProductCode(slotProps.item)"
                    :product-subtitle="
                        showProductSubtitle
                            ? resolveProductSubtitle(slotProps.item)
                            : ''
                    "
                    :product-desc="resolveProductDesc(slotProps.item)"
                    :product-type="resolveProductType(slotProps.item)"
                    :product-main-image="
                        resolveProductMainImage(slotProps.item)
                    "
                    :product-images="resolveProductImages(slotProps.item)"
                    :product-image-alt="resolveProductImageAlt(slotProps.item)"
                    :layout-mode="LAYOUT_MODE.GRID"
                    :image-mode="IMAGE_MODE.SLIDESHOW"
                    :lazy-tag="lazyTag"
                    v-bind="productCardImageProps"
                    style="width: 100%"
                >
                    <template v-if="showProductPrice" #product-price>
                        <template
                            v-if="resolveProductExceptionItem(slotProps.item)"
                        >
                            <span class="gh-product-card__unit-count">
                                {{ resolveExceptionQty(slotProps.item) }}
                            </span>
                            <span class="gh-product-card__unit">
                                {{ resolveExceptionUnit(slotProps.item) }}
                            </span>
                            起印・
                            <span>
                                {{ resolveExceptionPrice(slotProps.item) }}
                            </span>
                        </template>
                        <template v-else>
                            <span class="gh-product-card__unit-count">
                                {{ resolveMinOrderQtyText(slotProps.item) }}
                            </span>
                            <span class="gh-product-card__unit">
                                {{ resolveUnit(slotProps.item) }}
                            </span>
                            起印・$
                            <span class="gh-product-card__price">
                                {{ resolvePriceText(slotProps.item) }}
                            </span>
                        </template>
                    </template>
                </product-card>
            </a>
        </template>

        <template v-if="slots.empty" #empty="slotProps">
            <slot name="empty" v-bind="slotProps"></slot>
        </template>
    </section-frame>
</template>

<script setup lang="ts">
    import { computed, ref, useSlots, watch } from 'vue';
    import SectionFrame from '@/components/base/SectionFrame.vue';
    import { IMAGE_MODE } from '@/types/components/image-mode.types';
    import { LAYOUT_MODE } from '@/types/components/layout-mode.types';
    import ProductCard from '@/components/products/ProductCard.vue';
    import {
        BadgeVariant,
        ScrollBehaviorMode,
        SectionEmptyState,
        SidebarPosition,
        type ComponentValueMap,
        type MenuItem,
        type SectionFrameSidebarChangePayload,
    } from '@/types/components/base-components.types';

    interface ProductExceptionItem {
        qty: string | number;
        unit: string;
        price: string | number;
    }

    interface ProductFrameSidebarContext {
        [key: string]: string | number | boolean | null | undefined;
    }

    interface ProductFrameItem {
        productCode?: string;
        productDesc?: string;
        description?: string;
        productClassID?: string | number;
        imageDetailList?: string[];
        imageAlt?: string;
        mainImage?: string;
        productClassName?: string;
        subMainClassName?: string;
        productType?: string | number;
        url?: string;
        exceptionItem?: ProductExceptionItem | null;
        minOrderQtyText?: string | number;
        unit?: string;
        priceText?: string | number;
    }

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            cdnPrefixes?: string[];
            emptyState?: SectionEmptyState;
            emptyRetryText?: string;
            frameClass?: string | string[] | Record<string, boolean>;
            lazyTag?: string;
            moreIconName?: string;
            moreText?: string;
            moreUrl?: string;
            products?: ProductFrameItem[];
            showHeader?: boolean;
            showEmptyRetry?: boolean;
            showProductPrice?: boolean;
            showProductSubtitle?: boolean;
            showMore?: boolean;
            showSidebar?: boolean;
            sidebarActiveId?: string | number;
            sidebarActiveScrollBehavior?: ScrollBehaviorMode;
            sidebarAutoScrollActive?: boolean;
            sidebarBadgeVariant?: BadgeVariant;
            sidebarContext?: string | number | ProductFrameSidebarContext;
            sidebarItems?: MenuItem[];
            sidebarPosition?: SidebarPosition;
            title?: string;
            visibleRows?: number | string;
        }>(),
        {
            cdnPrefixes: () => [],
            emptyState: SectionEmptyState.Default,
            emptyRetryText: '重新讀取',
            frameClass: '',
            lazyTag: '',
            moreIconName: 'arrow-right-regular',
            moreText: '更多商品',
            moreUrl: '',
            products: () => [],
            showHeader: true,
            showEmptyRetry: false,
            showProductPrice: true,
            showProductSubtitle: true,
            showMore: true,
            showSidebar: false,
            sidebarActiveId: '',
            sidebarActiveScrollBehavior: ScrollBehaviorMode.Smooth,
            sidebarAutoScrollActive: false,
            sidebarBadgeVariant: BadgeVariant.Gray,
            sidebarContext: '',
            sidebarItems: () => [],
            sidebarPosition: SidebarPosition.Left,
            title: '',
            visibleRows: 0,
        }
    );

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'retry'): void;
        (
            event: 'change-sidebar',
            payload: SectionFrameSidebarChangePayload
        ): void;
    }>();

    // ----- attrs -----
    const slots = useSlots();

    // ----- state -----
    const internalSidebarActiveId = ref<string | number>(props.sidebarActiveId);

    // ----- computed -----
    // 將可見列數轉成正整數，無效值一律視為不限列
    const normalizedVisibleRows = computed(() => {
        const parsedValue = Number(props.visibleRows);
        return Number.isFinite(parsedValue) && parsedValue > 0
            ? Math.floor(parsedValue)
            : 0;
    });

    const hasVisibleRowLimit = computed(() => {
        return normalizedVisibleRows.value > 0;
    });

    const sectionFrameItems = computed<ComponentValueMap[]>(() => {
        return props.products as unknown as ComponentValueMap[];
    });

    const mergedFrameClass = computed(() => {
        return [
            props.frameClass,
            {
                'product-frame--row-limited': hasVisibleRowLimit.value,
            },
        ];
    });

    const mergedFrameStyle = computed(() => {
        if (!hasVisibleRowLimit.value) return '';

        return {
            '--product-frame-visible-rows': normalizedVisibleRows.value,
        };
    });

    // 只有明確提供 cdnPrefixes 時才往商品卡片傳遞
    const productCardImageProps = computed(() => {
        if (!Array.isArray(props.cdnPrefixes) || !props.cdnPrefixes.length) {
            return {};
        }

        return {
            cdnPrefixes: props.cdnPrefixes,
        };
    });

    // ----- methods -----
    const handleRetry = (): void => {
        emits('retry');
    };

    const resolveTextValue = (
        value:
            | string
            | number
            | boolean
            | null
            | undefined
            | Array<string | number | boolean | null | undefined>
            | ComponentValueMap
            | ComponentValueMap[]
    ): string => {
        if (typeof value === 'string' || typeof value === 'number') {
            return String(value);
        }

        return '';
    };

    const resolveTextOrNumberValue = (
        value:
            | string
            | number
            | boolean
            | null
            | undefined
            | Array<string | number | boolean | null | undefined>
            | ComponentValueMap
            | ComponentValueMap[]
    ): string | number => {
        if (typeof value === 'string' || typeof value === 'number') {
            return value;
        }

        return '';
    };

    // 略過無效 sidebar payload，僅在有效切換時同步 activeId 並向外通知
    const handleSidebarChange = (
        payload: SectionFrameSidebarChangePayload
    ): void => {
        const itemId = payload ? payload.itemId : '';
        if (typeof itemId === 'undefined' || itemId === null || itemId === '') {
            return;
        }

        internalSidebarActiveId.value = itemId as string | number;
        emits('change-sidebar', payload);
    };

    const resolveProductCode = (product: ComponentValueMap): string => {
        return resolveTextValue(product?.productCode);
    };

    const resolveProductDesc = (product: ComponentValueMap): string => {
        if (!product) return '';

        const productDesc = resolveTextValue(product.productDesc);
        if (productDesc) {
            return productDesc;
        }

        return resolveTextValue(product.description);
    };

    const resolveProductId = (product: ComponentValueMap): string => {
        const productId =
            product && product.productClassID ? product.productClassID : '';
        return String(productId);
    };

    const resolveProductImages = (product: ComponentValueMap): string[] => {
        if (!product || !Array.isArray(product.imageDetailList)) {
            return [];
        }

        return product.imageDetailList
            .filter((item): item is string => typeof item === 'string')
            .slice();
    };

    const resolveProductImageAlt = (product: ComponentValueMap): string => {
        return resolveTextValue(product?.imageAlt);
    };

    const resolveProductMainImage = (product: ComponentValueMap): string => {
        const mainImage = resolveTextValue(product?.mainImage);
        return mainImage || '/Images/ComingSoon.png';
    };

    const resolveProductName = (product: ComponentValueMap): string => {
        return resolveTextValue(product?.productClassName);
    };

    const resolveProductSubtitle = (product: ComponentValueMap): string => {
        return resolveTextValue(product?.subMainClassName);
    };

    const resolveProductType = (product: ComponentValueMap): number => {
        if (
            !product ||
            typeof product.productType === 'undefined' ||
            product.productType === null
        ) {
            return 0;
        }

        return Number(resolveTextOrNumberValue(product.productType));
    };

    const resolveProductUrl = (product: ComponentValueMap): string => {
        const url = resolveTextValue(product?.url);
        return url || 'javascript:void(0);';
    };

    const resolveProductExceptionItem = (
        product: ComponentValueMap
    ): ProductExceptionItem | null => {
        if (!product || !product.exceptionItem) {
            return null;
        }

        const rawValue = product.exceptionItem;
        if (
            !rawValue ||
            typeof rawValue !== 'object' ||
            Array.isArray(rawValue)
        ) {
            return null;
        }

        const qty = rawValue.qty;
        const unit = rawValue.unit;
        const price = rawValue.price;
        if (
            (typeof qty !== 'string' && typeof qty !== 'number') ||
            typeof unit !== 'string' ||
            (typeof price !== 'string' && typeof price !== 'number')
        ) {
            return null;
        }

        return {
            qty,
            unit,
            price,
        };
    };

    const resolveExceptionQty = (
        product: ComponentValueMap
    ): string | number => {
        const exceptionItem = resolveProductExceptionItem(product);
        return exceptionItem ? exceptionItem.qty : '';
    };

    const resolveExceptionUnit = (
        product: ComponentValueMap
    ): string | number => {
        const exceptionItem = resolveProductExceptionItem(product);
        return exceptionItem ? exceptionItem.unit : '';
    };

    const resolveExceptionPrice = (
        product: ComponentValueMap
    ): string | number => {
        const exceptionItem = resolveProductExceptionItem(product);
        return exceptionItem ? exceptionItem.price : '';
    };

    const resolveMinOrderQtyText = (
        product: ComponentValueMap
    ): string | number => {
        return resolveTextOrNumberValue(product?.minOrderQtyText);
    };

    const resolveUnit = (product: ComponentValueMap): string => {
        return resolveTextValue(product?.unit);
    };

    const resolvePriceText = (product: ComponentValueMap): string | number => {
        return resolveTextOrNumberValue(product?.priceText);
    };

    // ----- watch -----
    watch(
        () => props.sidebarActiveId,
        (newValue) => {
            internalSidebarActiveId.value = newValue;
        }
    );
</script>
